// src/components/admin/StaffAvailability.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Check, X, Save, AlertCircle } from 'lucide-react';
import { styles } from '../../styles/theme';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../services/firebase';

const DAYS = [
  { id: 'monday',    label: 'Mon' },
  { id: 'tuesday',   label: 'Tue' },
  { id: 'wednesday', label: 'Wed' },
  { id: 'thursday',  label: 'Thu' },
  { id: 'friday',    label: 'Fri' },
  { id: 'saturday',  label: 'Sat' },
];

// Convert HH:MM (24hr) → 12-hour display e.g. "2:00pm"
const to12Hour = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')}${ampm}`;
};

const getMondayOf = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  if (day === 0) {
    d.setDate(d.getDate() + 1); // Sunday → next Monday
  } else {
    d.setDate(d.getDate() - day + 1); // Mon–Sat → this Monday
  }
  d.setHours(0, 0, 0, 0);
  return d;
};

const toKey = (date) => {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}-${d}-${y}`;
};

const toLabel = (date) =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const getWeekMonday = (offset) => {
  const monday = getMondayOf(new Date());
  monday.setDate(monday.getDate() + offset * 7);
  return monday;
};

const createDefaultWeek = () => {
  const days = {};
  DAYS.forEach(({ id }) => {
    days[id] = { available: false, startTime: '', endTime: '' };
  });
  return days;
};

// Get the week key 7 days before a given key (format: MM-DD-YYYY)
const getPrevWeekKey = (key) => {
  const [m, d, y] = key.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() - 7);
  return `${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}-${date.getFullYear()}`;
};

const StaffAvailability = ({ onBack }) => {
  const [selectedWeekOffset, setSelectedWeekOffset] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const [availabilityMap, setAvailabilityMap] = useState({}); // { staffId: weekData }
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null); // { staffId, dayId }
  const [editValue, setEditValue] = useState({ available: false, startTime: '', endTime: '' });
  const [savingCell, setSavingCell] = useState(false);

  // Build week options (current + 3 ahead)
  const weeks = [0, 1, 2, 3].map((offset) => {
    const monday = getWeekMonday(offset);
    const saturday = new Date(monday);
    saturday.setDate(monday.getDate() + 5);
    return {
      offset,
      monday,
      key: toKey(monday),
      label: offset === 0
        ? `This Week (${toLabel(monday)} – ${toLabel(saturday)})`
        : offset === 1
        ? `Next Week (${toLabel(monday)} – ${toLabel(saturday)})`
        : `${toLabel(monday)} – ${toLabel(saturday)}`,
    };
  });

  const currentWeek = weeks[selectedWeekOffset];
  const weekKey = currentWeek.key;

  useEffect(() => {
    loadStaff();
    loadChangeRequests();
  }, []);

  useEffect(() => {
    if (staffList.length > 0) loadWeekAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffList, weekKey]);

  const loadStaff = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'staff_members'));
      const list = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => s.active)
        .sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
      setStaffList(list);
    } catch (err) {
      console.error('Error loading staff:', err);
    }
  };

  const loadWeekAvailability = async () => {
    setLoading(true);
    try {
      const map = {};
      const prevKey = getPrevWeekKey(weekKey);
      await Promise.all(
        staffList.map(async (staff) => {
          const ref = doc(db, 'staff_weekly_availability', staff.id, 'weeks', weekKey);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            map[staff.id] = snap.data();
          } else {
            // No data for this week — try to carry forward from previous week
            const prevRef = doc(db, 'staff_weekly_availability', staff.id, 'weeks', prevKey);
            const prevSnap = await getDoc(prevRef);
            if (prevSnap.exists() && prevSnap.data().days) {
              map[staff.id] = {
                days: prevSnap.data().days,
                submittedAt: null,
                carriedForwardFrom: prevKey,
              };
            } else {
              map[staff.id] = { days: createDefaultWeek(), submittedAt: null };
            }
          }
        })
      );
      setAvailabilityMap(map);
    } catch (err) {
      console.error('Error loading availability:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadChangeRequests = async () => {
    try {
      const q = query(
        collection(db, 'availability_change_requests'),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(q);
      const requests = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setChangeRequests(requests);
    } catch (err) {
      console.error('Error loading change requests:', err);
    }
  };

  const handleApproveRequest = async (request) => {
    try {
      await updateDoc(doc(db, 'availability_change_requests', request.id), {
        status: 'approved',
        resolvedAt: serverTimestamp(),
      });
      setChangeRequests(prev => prev.filter(r => r.id !== request.id));
      alert(`✅ Approved change request for ${request.staffName} — ${request.dayLabel}`);
    } catch (err) {
      console.error('Error approving request:', err);
    }
  };

  const handleDenyRequest = async (request) => {
    try {
      await updateDoc(doc(db, 'availability_change_requests', request.id), {
        status: 'denied',
        resolvedAt: serverTimestamp(),
      });
      setChangeRequests(prev => prev.filter(r => r.id !== request.id));
    } catch (err) {
      console.error('Error denying request:', err);
    }
  };

  const startEdit = (staffId, dayId) => {
    const current = availabilityMap[staffId]?.days?.[dayId] || { available: false, startTime: '', endTime: '' };
    setEditingCell({ staffId, dayId });
    setEditValue({ ...current });
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue({ available: false, startTime: '', endTime: '' });
  };

  const saveOverride = async () => {
    if (!editingCell) return;
    setSavingCell(true);
    try {
      const { staffId, dayId } = editingCell;
      const staff = staffList.find(s => s.id === staffId);
      const ref = doc(db, 'staff_weekly_availability', staffId, 'weeks', weekKey);
      const existing = availabilityMap[staffId] || { days: createDefaultWeek() };

      const updatedDays = {
        ...existing.days,
        [dayId]: { ...editValue },
      };

      await setDoc(ref, {
        ...existing,
        days: updatedDays,
        staffId,
        staffName: `${staff.firstName} ${staff.lastName}`,
        weekStart: weekKey,
        adminOverride: true,
        overrideAt: serverTimestamp(),
      }, { merge: true });

      setAvailabilityMap(prev => ({
        ...prev,
        [staffId]: {
          ...prev[staffId],
          days: updatedDays,
          adminOverride: true,
        }
      }));

      setEditingCell(null);
    } catch (err) {
      console.error('Error saving override:', err);
      alert('Failed to save. Please try again.');
    } finally {
      setSavingCell(false);
    }
  };

  const getDayCell = (staffId, dayId) => {
    const day = availabilityMap[staffId]?.days?.[dayId];
    if (!day) return null;
    return day;
  };

  const submittedCount = staffList.filter(s => availabilityMap[s.id]?.submittedAt).length;

  return (
    <div style={{ padding: '1rem' }}>

      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onBack} style={styles.secondaryButton}>
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <div style={{ marginTop: '1rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem' }}>
            Staff Availability
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
            {submittedCount} of {staffList.length} staff submitted for this week
          </p>
        </div>
      </div>

      {/* Change Request Queue */}
      {changeRequests.length > 0 && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <AlertCircle size={18} style={{ color: '#d97706' }} />
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#92400e' }}>
              Pending Change Requests ({changeRequests.length})
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {changeRequests.map(req => (
              <div key={req.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.75rem 1rem',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #fde68a',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#1f2937' }}>
                    {req.staffName} — {req.dayLabel}
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#6b7280', fontWeight: '400' }}>
                      (week of {req.weekStart})
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem' }}>
                    {req.reason}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <button
                    onClick={() => handleApproveRequest(req)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.4rem 0.8rem',
                      backgroundColor: '#10b981', color: 'white',
                      border: 'none', borderRadius: '6px',
                      fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer',
                    }}
                  >
                    <Check size={14} /> Approve
                  </button>
                  <button
                    onClick={() => handleDenyRequest(req)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.4rem 0.8rem',
                      backgroundColor: '#ef4444', color: 'white',
                      border: 'none', borderRadius: '6px',
                      fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer',
                    }}
                  >
                    <X size={14} /> Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Week Selector */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {weeks.map(w => (
          <button
            key={w.offset}
            onClick={() => setSelectedWeekOffset(w.offset)}
            style={{
              padding: '0.5rem 1rem',
              border: '2px solid',
              borderColor: selectedWeekOffset === w.offset ? '#3b82f6' : '#e5e7eb',
              borderRadius: '8px',
              backgroundColor: selectedWeekOffset === w.offset ? '#eff6ff' : 'white',
              color: selectedWeekOffset === w.offset ? '#1e40af' : '#374151',
              fontSize: '0.8rem',
              fontWeight: selectedWeekOffset === w.offset ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {w.label}
          </button>
        ))}
      </div>

      {/* Availability Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          Loading availability...
        </div>
      ) : staffList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          No active staff found.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr>
                <th style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'left',
                  fontSize: '0.8rem',
                  fontWeight: '700',
                  color: '#374151',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  minWidth: '130px',
                }}>
                  Staff
                </th>
                <th style={{
                  padding: '0.75rem 0.5rem',
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  fontWeight: '700',
                  color: '#374151',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  width: '80px',
                }}>
                  Submitted
                </th>
                {DAYS.map(day => {
                  const dayDate = new Date(currentWeek.monday);
                  dayDate.setDate(currentWeek.monday.getDate() + DAYS.findIndex(d => d.id === day.id));
                  return (
                    <th key={day.id} style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      fontSize: '0.8rem',
                      fontWeight: '700',
                      color: '#374151',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                    }}>
                      <div>{day.label}</div>
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: '400' }}>
                        {dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {staffList.map((staff, rowIdx) => {
                const weekData = availabilityMap[staff.id];
                const submitted = !!weekData?.submittedAt;
                const isAdminOverride = !!weekData?.adminOverride;

                return (
                  <tr key={staff.id} style={{ backgroundColor: rowIdx % 2 === 0 ? 'white' : '#f9fafb' }}>
                    {/* Staff Name */}
                    <td style={{
                      padding: '0.75rem 1rem',
                      border: '1px solid #e5e7eb',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      color: '#1f2937',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {staff.firstName} {staff.lastName}
                        {staff.role && staff.role !== 'instructor' && (() => {
                          const roleColors = {
                            driver:     { bg: '#ede9fe', text: '#7c3aed' },
                            admin_staff:{ bg: '#dbeafe', text: '#1e40af' },
                            operations: { bg: '#d1fae5', text: '#065f46' },
                            manager:    { bg: '#fef3c7', text: '#92400e' },
                          };
                          const colors = roleColors[staff.role] || { bg: '#f3f4f6', text: '#374151' };
                          const label = staff.role.replace('_', ' ');
                          return (
                            <span style={{
                              fontSize: '0.65rem',
                              fontWeight: '700',
                              color: colors.text,
                              backgroundColor: colors.bg,
                              padding: '0.1rem 0.4rem',
                              borderRadius: '4px',
                              textTransform: 'capitalize',
                              letterSpacing: '0.03em',
                            }}>
                              {label}
                            </span>
                          );
                        })()}
                      </div>
                      {isAdminOverride && (
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: '400' }}>
                          admin edited
                        </div>
                      )}
                    </td>

                    {/* Submitted status */}
                    <td style={{
                      padding: '0.5rem',
                      border: '1px solid #e5e7eb',
                      textAlign: 'center',
                    }}>
                      {submitted ? (
                        <span style={{ fontSize: '1rem' }} title="Submitted">✅</span>
                      ) : weekData?.carriedForwardFrom ? (
                        <span style={{ fontSize: '0.7rem', color: '#1e40af', fontWeight: '600' }} title="Carried from previous week">📋</span>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>—</span>
                      )}
                    </td>

                    {/* Day Cells */}
                    {DAYS.map(day => {
                      const cell = getDayCell(staff.id, day.id);
                      const isEditing = editingCell?.staffId === staff.id && editingCell?.dayId === day.id;

                      if (isEditing) {
                        return (
                          <td key={day.id} style={{
                            padding: '0.4rem',
                            border: '2px solid #3b82f6',
                            backgroundColor: '#eff6ff',
                            minWidth: '120px',
                          }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', fontWeight: '600' }}>
                                <input
                                  type="checkbox"
                                  checked={editValue.available}
                                  onChange={e => setEditValue(v => ({
                                    ...v,
                                    available: e.target.checked,
                                    ...(!e.target.checked ? { startTime: '', endTime: '' } : {})
                                  }))}
                                />
                                Available
                              </label>
                              {editValue.available && (
                                <>
                                  <input
                                    type="time"
                                    value={editValue.startTime}
                                    onChange={e => setEditValue(v => ({ ...v, startTime: e.target.value }))}
                                    style={{ fontSize: '0.75rem', padding: '0.2rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                  />
                                  <input
                                    type="time"
                                    value={editValue.endTime}
                                    onChange={e => setEditValue(v => ({ ...v, endTime: e.target.value }))}
                                    style={{ fontSize: '0.75rem', padding: '0.2rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                  />
                                </>
                              )}
                              <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.2rem' }}>
                                <button
                                  onClick={saveOverride}
                                  disabled={savingCell}
                                  style={{
                                    flex: 1, padding: '0.3rem',
                                    backgroundColor: '#10b981', color: 'white',
                                    border: 'none', borderRadius: '4px',
                                    fontSize: '0.7rem', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.2rem',
                                  }}
                                >
                                  <Save size={11} /> {savingCell ? '...' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  style={{
                                    flex: 1, padding: '0.3rem',
                                    backgroundColor: '#6b7280', color: 'white',
                                    border: 'none', borderRadius: '4px',
                                    fontSize: '0.7rem', cursor: 'pointer',
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        );
                      }

                      return (
                        <td
                          key={day.id}
                          onClick={() => startEdit(staff.id, day.id)}
                          title="Click to override"
                          style={{
                            padding: '0.5rem',
                            border: '1px solid #e5e7eb',
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: cell?.available ? '#f0fdf4' : 'transparent',
                            transition: 'background-color 0.1s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = cell?.available ? '#dcfce7' : '#f3f4f6'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = cell?.available ? '#f0fdf4' : 'transparent'}
                        >
                          {cell?.available ? (
                            <div>
                              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#15803d' }}>✓</div>
                              {(cell.startTime || cell.endTime) && (
                                <div style={{ fontSize: '0.65rem', color: '#6b7280', marginTop: '0.1rem', lineHeight: 1.2 }}>
                                  {to12Hour(cell.startTime)}{cell.startTime && cell.endTime ? '–' : ''}{to12Hour(cell.endTime)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ fontSize: '0.75rem', color: '#d1d5db' }}>—</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div style={{
        marginTop: '1rem',
        display: 'flex',
        gap: '1.5rem',
        fontSize: '0.75rem',
        color: '#6b7280',
        flexWrap: 'wrap',
      }}>
        <span>✅ = availability submitted</span>
        <span style={{ color: '#15803d' }}>✓ = available that day</span>
        <span>— = unavailable</span>
        <span style={{ color: '#3b82f6' }}>Click any cell to override</span>
      </div>
    </div>
  );
};

export default StaffAvailability;